"use client";

import { useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const token = sessionStorage.getItem("token");
const BANKS = ["HBL", "UBL", "NBP", "BAL"];

const REQUIRED_HEADERS = ["date", "description", "pv_no", "transaction_id", "deposits"];
const SAMPLE_CSV =
  "date,description,pv_no,transaction_id,deposits\n6/9/2026,Semester fee deposit,PV001,TXN123456,5000.00\n6/10/2026,Application fee,PV002,TXN789012,2500.00";
const DATE_PATTERN = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

const isValidCsvDate = (dateStr) => {
  const match = DATE_PATTERN.exec(dateStr);
  if (!match) return false;

  const month = Number.parseInt(match[1], 10);
  const day = Number.parseInt(match[2], 10);
  const year = Number.parseInt(match[3], 10);
  const date = new Date(year, month - 1, day);

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

const toDateOnly = (dateStr) => {
  if (!dateStr) return "";
  const raw = String(dateStr).trim();
  if (raw.includes("T")) {
    return raw.split("T")[0];
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return raw;
  }
  const match = DATE_PATTERN.exec(raw);
  if (match) {
    const month = String(match[1]).padStart(2, "0");
    const day = String(match[2]).padStart(2, "0");
    return `${match[3]}-${month}-${day}`;
  }
  return raw;
};

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "-";

  if (DATE_PATTERN.test(dateStr)) {
    return dateStr;
  }

  const dateOnly = toDateOnly(dateStr);
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
    const [year, month, day] = dateOnly.split("-");
    return `${Number.parseInt(month, 10)}/${Number.parseInt(day, 10)}/${year}`;
  }

  const parsed = new Date(dateStr);
  if (Number.isNaN(parsed.getTime())) return dateStr;

  return `${parsed.getMonth() + 1}/${parsed.getDate()}/${parsed.getFullYear()}`;
};

const prefixTransactionId = (bank, transactionId) => {
  if (!transactionId) return "";
  const raw = String(transactionId).trim();
  if (!raw) return "";
  const prefix = `${bank}-`;
  if (raw.toUpperCase().startsWith(prefix)) return raw;
  return `${prefix}${raw}`;
};

const primaryButtonStyle = {
  width: "100%",
  height: "45px",
  color: "#ffffff",
  textTransform: "none",
  fontWeight: 500,
  boxShadow: "none",
  display: "inline-flex",
  visibility: "visible",
};

const errorButtonStyle = {
  color: "#ffffff",
  backgroundColor: "#d32f2f",
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "none",
  minWidth: "140px",
};

const errorButtonDisabledStyle = {
  ...errorButtonStyle,
  backgroundColor: "#9e9e9e",
  color: "#ffffff",
};

const getUploadRecordsButtonStyle = (isDisabled) => ({
  width: "100%",
  height: "45px",
  color: "#ffffff",
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "none",
  display: "inline-flex",
  visibility: "visible",
  backgroundColor: isDisabled ? "#9e9e9e" : "#2e7d32",
  cursor: isDisabled ? "not-allowed" : "pointer",
});

function UploadBankFile() {
  const [selectedBank, setSelectedBank] = useState("");
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [csvErrors, setCsvErrors] = useState([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [uploadResults, setUploadResults] = useState({ success: [], errors: [] });
  const [filterBank, setFilterBank] = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [deleteBank, setDeleteBank] = useState("");
  const [deleteStartDate, setDeleteStartDate] = useState("");
  const [deleteEndDate, setDeleteEndDate] = useState("");
  const [deleteSelectedDates, setDeleteSelectedDates] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [datesLoading, setDatesLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const downloadTemplate = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "bank_file_template.csv";
    anchor.click();
    window.URL.revokeObjectURL(url);
  };

  const parseCsvFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const text = event.target.result;
          const lines = text.split(/\r?\n/).filter((line) => line.trim());
          if (lines.length < 2) {
            reject("CSV file must contain a header row and at least one data row");
            return;
          }

          const headers = lines[0].split(",").map((header) => header.trim().toLowerCase());
          const missingHeaders = REQUIRED_HEADERS.filter((header) => !headers.includes(header));
          if (missingHeaders.length > 0) {
            reject(`Missing required columns: ${missingHeaders.join(", ")}`);
            return;
          }

          const data = [];
          const errors = [];

          for (let i = 1; i < lines.length; i++) {
            const values = lines[i].split(",").map((value) => value.trim());
            const row = {};
            headers.forEach((header, index) => {
              row[header] = values[index] || "";
            });

            const rowErrors = [];
            if (!row.date) rowErrors.push("Date is required");
            if (!isValidCsvDate(row.date)) {
              rowErrors.push("Date must be in M/D/YYYY format (example: 6/9/2026)");
            }
            if (!row.description) rowErrors.push("Description is required");
            if (row.deposits !== "" && Number.isNaN(Number.parseFloat(row.deposits))) {
              rowErrors.push("Deposits must be a valid number");
            }

            if (rowErrors.length > 0) {
              errors.push(`Row ${i + 1}: ${rowErrors.join(", ")}`);
            } else {
              data.push({
                date: row.date,
                description: row.description,
                pv_no: row.pv_no || "",
                transaction_id: row.transaction_id || "",
                deposits: row.deposits === "" ? 0 : Number.parseFloat(row.deposits),
              });
            }
          }

          resolve({ data, errors });
        } catch (error) {
          reject("Error parsing CSV file");
        }
      };
      reader.onerror = () => reject("Unable to read CSV file");
      reader.readAsText(file);
    });
  };

  const handleCsvUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
      toast.error("Please upload a CSV file");
      return;
    }

    setCsvFile(file);
    setUploadLoading(true);
    setUploadResults({ success: [], errors: [] });

    try {
      const { data, errors } = await parseCsvFile(file);
      setCsvData(data);
      setCsvErrors(errors);

      if (errors.length > 0) {
        toast.warning(`CSV uploaded with ${errors.length} error(s). Please review before uploading.`);
      } else {
        toast.success(`CSV parsed successfully. ${data.length} record(s) ready to upload.`);
      }
    } catch (error) {
      toast.error(error);
      setCsvData([]);
      setCsvErrors([]);
    } finally {
      setUploadLoading(false);
      event.target.value = "";
    }
  };

  const fetchRecords = async () => {
    setRecordsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterBank) params.append("bank", filterBank);
      if (filterStartDate) params.append("start_date", filterStartDate);
      if (filterEndDate) params.append("end_date", filterEndDate);
      const query = params.toString();
      const url = `${BASE_URL}/fee_voucher/get_transaction_records${query ? `?${query}` : ""}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();

      if (response.ok) {
        setRecords(data.records || []);
      } else {
        toast.error(data.error || "Failed to load transaction records");
      }
    } catch (error) {
      console.error("Error fetching transaction records:", error);
      toast.error("Error fetching transaction records");
    } finally {
      setRecordsLoading(false);
    }
  };

  const handleBulkUpload = async () => {
    if (!selectedBank) {
      toast.error("Please select a bank before uploading");
      return;
    }
    if (csvData.length === 0) {
      toast.error("No valid records to upload");
      return;
    }
    if (csvErrors.length > 0) {
      toast.error("Please fix all CSV errors before uploading");
      return;
    }

    setUploadLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/fee_voucher/bulk_upload_transaction_records`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ bank: selectedBank, records: csvData }),
      });
      const data = await response.json();

      if (response.ok) {
        toast.success(`Uploaded ${data.success?.length || 0} transaction record(s) successfully`);
        setUploadResults({
          success: data.success || [],
          errors: data.errors || [],
        });
        setCsvFile(null);
        setCsvData([]);
        setCsvErrors([]);
        fetchRecords();
        if (deleteBank) {
          fetchAvailableDates(deleteBank);
        }
      } else {
        setUploadResults({
          success: data.success || [],
          errors: data.errors || [],
        });
        toast.error(data.error || data.message || "Error uploading transaction records");
      }
    } catch (error) {
      console.error("Error uploading transaction records:", error);
      toast.error("Error uploading transaction records");
    } finally {
      setUploadLoading(false);
    }
  };

  const fetchAvailableDates = async (bank) => {
    setDatesLoading(true);
    try {
      const url = bank
        ? `${BASE_URL}/fee_voucher/get_transaction_record_dates?bank=${encodeURIComponent(bank)}`
        : `${BASE_URL}/fee_voucher/get_transaction_record_dates`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();

      if (response.ok) {
        const dates = (data.dates || []).map(toDateOnly).filter(Boolean);
        setAvailableDates(dates);
        setDeleteSelectedDates((current) => current.filter((dateValue) => dates.includes(dateValue)));
      } else {
        setAvailableDates([]);
        setDeleteSelectedDates([]);
        toast.error(data.error || "Failed to load available dates");
      }
    } catch (error) {
      console.error("Error fetching transaction record dates:", error);
      setAvailableDates([]);
      setDeleteSelectedDates([]);
      toast.error("Error fetching available dates");
    } finally {
      setDatesLoading(false);
    }
  };

  const filteredDeleteDates = useMemo(() => {
    return availableDates.filter((dateValue) => {
      if (deleteStartDate && dateValue < deleteStartDate) return false;
      if (deleteEndDate && dateValue > deleteEndDate) return false;
      return true;
    });
  }, [availableDates, deleteStartDate, deleteEndDate]);

  const handleDeleteDateRangeChange = (field, value) => {
    if (field === "start") {
      setDeleteStartDate(value);
      if (value && deleteEndDate && value > deleteEndDate) {
        setDeleteEndDate(value);
      }
    } else {
      setDeleteEndDate(value);
      if (value && deleteStartDate && value < deleteStartDate) {
        setDeleteStartDate(value);
      }
    }
    setDeleteSelectedDates([]);
  };

  const handleDeleteRecords = async () => {
    if (!deleteStartDate || !deleteEndDate) {
      toast.error("Please select from and to dates");
      return;
    }
    if (deleteStartDate > deleteEndDate) {
      toast.error("From date cannot be after to date");
      return;
    }
    if (deleteSelectedDates.length === 0) {
      toast.error("Please select at least one transaction date");
      return;
    }

    const normalizedDates = deleteSelectedDates.map(toDateOnly).filter(Boolean);

    setDeleteLoading(true);
    try {
      const payload = {
        dates: normalizedDates,
        start_date: toDateOnly(deleteStartDate),
        end_date: toDateOnly(deleteEndDate),
      };
      if (deleteBank) {
        payload.bank = deleteBank;
      }

      const response = await fetch(`${BASE_URL}/fee_voucher/delete_transaction_records`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || `Deleted ${data.deleted_count || 0} record(s)`);
        setDeleteDialogOpen(false);
        setDeleteSelectedDates([]);
        fetchAvailableDates(deleteBank);
        fetchRecords();
      } else {
        toast.error(data.error || "Failed to delete transaction records");
      }
    } catch (error) {
      console.error("Error deleting transaction records:", error);
      toast.error("Error deleting transaction records");
    } finally {
      setDeleteLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  useEffect(() => {
    fetchAvailableDates(deleteBank);
  }, [deleteBank]);

  const previewColumns = useMemo(
    () => [
      { Header: "#", accessor: "serial", align: "left" },
      { Header: "Date", accessor: "date", align: "left" },
      { Header: "Description", accessor: "description", align: "left" },
      { Header: "PV No", accessor: "pv_no", align: "left" },
      { Header: "Transaction ID", accessor: "transaction_id", align: "left" },
      { Header: "Deposits", accessor: "deposits", align: "right" },
    ],
    []
  );

  const previewRows =
    csvData.length > 0
      ? csvData.map((row, index) => ({
          serial: index + 1,
          date: formatDisplayDate(row.date),
          description: row.description,
          pv_no: row.pv_no || "-",
          transaction_id: row.transaction_id
            ? prefixTransactionId(selectedBank, row.transaction_id)
            : "-",
          deposits: row.deposits,
        }))
      : [{ serial: "-", date: "-", description: "-", pv_no: "-", transaction_id: "-", deposits: "-" }];

  const recordColumns = useMemo(
    () => [
      { Header: "#", accessor: "serial", align: "left" },
      { Header: "Date", accessor: "date", align: "left" },
      { Header: "Description", accessor: "description", align: "left" },
      { Header: "PV No", accessor: "pv_no", align: "left" },
      { Header: "Transaction ID", accessor: "transaction_id", align: "left" },
      { Header: "Deposits", accessor: "deposits", align: "right" },
      { Header: "Uploaded At", accessor: "createdAt", align: "left" },
    ],
    []
  );

  const recordRows =
    records.length > 0
      ? records.map((record, index) => ({
          serial: index + 1,
          date: formatDisplayDate(record.date),
          description: record.description,
          pv_no: record.pv_no || "-",
          transaction_id: record.transaction_id || "-",
          deposits: record.deposits,
          createdAt: record.createdAt ? new Date(record.createdAt).toLocaleString() : "-",
        }))
      : [{ serial: "-", date: "-", description: "-", pv_no: "-", transaction_id: "-", deposits: "-", createdAt: "-" }];

  const isUploadRecordsDisabled =
    uploadLoading || !selectedBank || csvData.length === 0 || csvErrors.length > 0;

  const isDeleteDisabled =
    !deleteStartDate ||
    !deleteEndDate ||
    deleteSelectedDates.length === 0 ||
    deleteLoading;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ToastContainer />
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card style={{ padding: "24px", overflow: "visible" }}>
              <MDTypography variant="h5" fontWeight="medium" mb={2}>
                Upload Bank File
              </MDTypography>

              <Grid container spacing={2} style={{ marginBottom: "16px" }}>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Select Bank</InputLabel>
                    <Select
                      value={selectedBank}
                      onChange={(e) => setSelectedBank(e.target.value)}
                      label="Select Bank"
                      style={{ height: "45px" }}
                    >
                      {BANKS.map((bank) => (
                        <MenuItem key={bank} value={bank}>
                          {bank}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<DownloadIcon />}
                    onClick={downloadTemplate}
                    style={primaryButtonStyle}
                  >
                    Download Sample
                  </Button>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <input
                    accept=".csv"
                    style={{ display: "none" }}
                    id="upload-bank-file-csv"
                    type="file"
                    onChange={handleCsvUpload}
                  />
                  <label htmlFor="upload-bank-file-csv" style={{ display: "block", width: "100%", margin: 0 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      style={primaryButtonStyle}
                    >
                      Upload CSV
                    </Button>
                  </label>
                </Grid>

                <Grid item xs={12} sm={12} md={4}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<RefreshIcon />}
                    onClick={fetchRecords}
                    disabled={recordsLoading}
                    style={primaryButtonStyle}
                  >
                    {recordsLoading ? <CircularProgress size={20} color="inherit" /> : "Refresh"}
                  </Button>
                </Grid>
              </Grid>

              {csvFile && (
                <Box style={{ marginBottom: "16px" }}>
                  <Chip
                    label={`Selected file: ${csvFile.name}`}
                    color="info"
                    variant="outlined"
                    onDelete={() => {
                      setCsvFile(null);
                      setCsvData([]);
                      setCsvErrors([]);
                    }}
                  />
                </Box>
              )}

              <Grid container spacing={2} style={{ marginBottom: "16px" }}>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    startIcon={uploadLoading ? null : <CloudUploadIcon />}
                    onClick={() => {
                      if (!isUploadRecordsDisabled) {
                        handleBulkUpload();
                      }
                    }}
                    style={getUploadRecordsButtonStyle(isUploadRecordsDisabled)}
                  >
                    {uploadLoading ? <CircularProgress size={20} style={{ color: "#ffffff" }} /> : "Upload Records"}
                  </Button>
                </Grid>
              </Grid>

              <Alert severity="info" sx={{ mb: 3 }}>
                <AlertTitle>Upload Instructions</AlertTitle>
                <MDTypography variant="body2" mb={1}>
                  1. Select the bank for which you are uploading this file.
                </MDTypography>
                <MDTypography variant="body2" mb={1}>
                  2. Download the sample CSV and keep the same column names.
                </MDTypography>
                <MDTypography variant="body2" mb={1}>
                  3. Required columns: date, description, pv_no, transaction_id, deposits
                </MDTypography>
                <MDTypography variant="body2" mb={1}>
                  4. Date format: M/D/YYYY (example: 6/9/2026)
                </MDTypography>
                <MDTypography variant="body2" mb={1}>
                  5. Transaction IDs are saved as BANK-transaction_id (example: HBL-TXN123456).
                </MDTypography>
                <MDTypography variant="body2">
                  6. Upload the CSV, review the preview, then click Upload Records.
                </MDTypography>
              </Alert>

              {uploadLoading && (
                <Box display="flex" alignItems="center" mb={2}>
                  <CircularProgress size={24} />
                  <MDTypography variant="body2" ml={2}>
                    Processing...
                  </MDTypography>
                </Box>
              )}

              {csvErrors.length > 0 && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <AlertTitle>CSV Validation Errors</AlertTitle>
                  {csvErrors.map((error) => (
                    <MDTypography key={error} variant="body2">
                      {error}
                    </MDTypography>
                  ))}
                </Alert>
              )}

              {uploadResults.errors.length > 0 && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  <AlertTitle>Upload Errors</AlertTitle>
                  {uploadResults.errors.map((item) => (
                    <MDTypography key={`${item.row}-${item.error}`} variant="body2">
                      Row {item.row}: {item.error}
                    </MDTypography>
                  ))}
                </Alert>
              )}

              {uploadResults.success.length > 0 && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  <AlertTitle>Upload Summary</AlertTitle>
                  <MDTypography variant="body2">
                    {uploadResults.success.length} record(s) uploaded successfully.
                  </MDTypography>
                </Alert>
              )}

              <MDTypography variant="h6" mb={2}>
                CSV Preview
              </MDTypography>
              <DataTable
                table={{ columns: previewColumns, rows: previewRows }}
                isSorted={false}
                entriesPerPage={false}
                showTotalEntries={false}
                noEndBorder
              />
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ p: 3 }}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <MDTypography variant="h6">Uploaded Transaction Records</MDTypography>
                {recordsLoading && <CircularProgress size={22} />}
              </MDBox>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Filter Bank</InputLabel>
                    <Select
                      value={filterBank}
                      onChange={(e) => setFilterBank(e.target.value)}
                      label="Filter Bank"
                      style={{ height: "45px" }}
                    >
                      <MenuItem value="">
                        <em>All Banks</em>
                      </MenuItem>
                      {BANKS.map((bank) => (
                        <MenuItem key={bank} value={bank}>
                          {bank}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Payment From Date"
                    type="date"
                    value={filterStartDate}
                    onChange={(e) => setFilterStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Payment To Date"
                    type="date"
                    value={filterEndDate}
                    onChange={(e) => setFilterEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<RefreshIcon />}
                    onClick={fetchRecords}
                    disabled={recordsLoading}
                    style={primaryButtonStyle}
                  >
                    Apply Filter
                  </Button>
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <MDTypography variant="subtitle2" fontWeight="medium" mb={1}>
                    Delete Uploaded Records
                  </MDTypography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Delete Bank (Optional)</InputLabel>
                    <Select
                      value={deleteBank}
                      onChange={(e) => {
                        setDeleteBank(e.target.value);
                        setDeleteStartDate("");
                        setDeleteEndDate("");
                        setDeleteSelectedDates([]);
                      }}
                      label="Delete Bank (Optional)"
                      style={{ height: "45px" }}
                    >
                      <MenuItem value="">
                        <em>All Banks</em>
                      </MenuItem>
                      {BANKS.map((bank) => (
                        <MenuItem key={bank} value={bank}>
                          {bank}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="From Date"
                    type="date"
                    value={deleteStartDate}
                    onChange={(e) => handleDeleteDateRangeChange("start", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="To Date"
                    type="date"
                    value={deleteEndDate}
                    onChange={(e) => handleDeleteDateRangeChange("end", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth disabled={datesLoading || filteredDeleteDates.length === 0}>
                    <InputLabel>Transaction Dates</InputLabel>
                    <Select
                      multiple
                      value={deleteSelectedDates}
                      onChange={(e) => setDeleteSelectedDates(e.target.value)}
                      label="Transaction Dates"
                      renderValue={(selected) =>
                        selected.length === 0
                          ? "Select dates"
                          : selected.map((dateValue) => formatDisplayDate(dateValue)).join(", ")
                      }
                      style={{ minHeight: "45px" }}
                    >
                      {filteredDeleteDates.length === 0 ? (
                        <MenuItem disabled value="">
                          <em>
                            {datesLoading
                              ? "Loading dates..."
                              : deleteStartDate && deleteEndDate
                                ? "No records in selected range"
                                : "Select from and to dates first"}
                          </em>
                        </MenuItem>
                      ) : (
                        filteredDeleteDates.map((dateValue) => (
                          <MenuItem key={dateValue} value={dateValue}>
                            <Checkbox checked={deleteSelectedDates.includes(dateValue)} />
                            <ListItemText primary={formatDisplayDate(dateValue)} />
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Button
                    variant="contained"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteDialogOpen(true)}
                    disabled={isDeleteDisabled}
                    style={{
                      ...primaryButtonStyle,
                      ...(isDeleteDisabled ? errorButtonDisabledStyle : errorButtonStyle),
                    }}
                  >
                    Delete Selected Records
                  </Button>
                </Grid>
              </Grid>

              <DataTable
                table={{ columns: recordColumns, rows: recordRows }}
                isSorted={false}
                entriesPerPage={{ defaultValue: 10, entries: [5, 10, 25, 50] }}
                showTotalEntries
                noEndBorder
              />
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog open={deleteDialogOpen} onClose={() => !deleteLoading && setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Transaction Records</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" mb={2}>
            This will permanently delete all uploaded bank records where:
          </MDTypography>
          <MDTypography variant="body2" mb={1}>
            Bank: <strong>{deleteBank || "All Banks"}</strong>
            {deleteBank ? " (from transaction ID prefix)" : ""}
          </MDTypography>
          <MDTypography variant="body2" mb={1}>
            Date range: <strong>{formatDisplayDate(deleteStartDate)}</strong> to{" "}
            <strong>{formatDisplayDate(deleteEndDate)}</strong>
          </MDTypography>
          <MDTypography variant="body2" mb={2}>
            Selected dates ({deleteSelectedDates.length}):{" "}
            <strong>
              {deleteSelectedDates.length > 0
                ? deleteSelectedDates.map((dateValue) => formatDisplayDate(dateValue)).join(", ")
                : "-"}
            </strong>
          </MDTypography>
          <Alert severity="warning">This action cannot be undone.</Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleteLoading}
            style={{ textTransform: "none", color: "#344767" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteRecords}
            variant="contained"
            disabled={deleteLoading}
            style={deleteLoading ? errorButtonDisabledStyle : errorButtonStyle}
          >
            {deleteLoading ? <CircularProgress size={20} sx={{ color: "#ffffff" }} /> : "Delete Records"}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default UploadBankFile;
